import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ShieldCheck, Briefcase, Calculator, TrendingUp, Check, X, Info } from 'lucide-react';
import { ROLES, PERMISSIONS, ROLE_PERMISSIONS, PERMISSION_LABELS, PERMISSION_CATEGORIES } from './permissions';

const ROLE_ICONS = {
  admin: ShieldCheck,
  project_manager: Briefcase,
  accountant: Calculator,
  investor: TrendingUp
};

export function RoleGuideModal({ open, onOpenChange }) {
  const roles = ['admin', 'project_manager', 'accountant', 'investor'];
  
  const hasRolePermission = (roleId, permission) => {
    return ROLE_PERMISSIONS[roleId]?.includes(permission);
  };
  
  const getPermissionStatus = (roleId, permission) => {
    const hasPerm = hasRolePermission(roleId, permission);
    const role = ROLES[roleId];
    
    if (hasPerm) {
      // Check if it's project-specific for PM
      if (roleId === 'project_manager' && role.requiresProjectAssignment) {
        return { status: 'assigned', label: 'Assigned Only' };
      }
      return { status: 'yes', label: 'Yes' };
    }
    return { status: 'no', label: 'No' };
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Info className="w-5 h-5 text-blue-600" />
            Role Permissions Guide
          </DialogTitle>
          <DialogDescription>
            Understanding what each team role can and cannot do in DrawReady
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          {/* Role Cards Summary */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {roles.map(roleId => {
              const role = ROLES[roleId];
              const Icon = ROLE_ICONS[roleId];
              return (
                <div 
                  key={roleId}
                  className={`p-3 rounded-lg border-2 ${role.color.border} ${role.color.bg}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${role.color.text}`} />
                    <span className={`font-semibold text-sm ${role.color.text}`}>
                      {role.name}
                    </span>
                  </div>
                  <p className={`text-xs ${role.color.text} opacity-80`}>
                    {roleId === 'admin' && 'Full access'}
                    {roleId === 'project_manager' && 'Assigned projects'}
                    {roleId === 'accountant' && 'View financials'}
                    {roleId === 'investor' && 'View only'}
                  </p>
                </div>
              );
            })}
          </div>
          
          {/* Permission Matrix Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b">
                  <th className="text-left p-3 font-semibold text-slate-700">Permission</th>
                  {roles.map(roleId => {
                    const role = ROLES[roleId];
                    const Icon = ROLE_ICONS[roleId];
                    return (
                      <th key={roleId} className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <Icon className={`w-4 h-4 ${role.color.text}`} />
                          <span className={`font-semibold ${roleId === 'admin' ? 'text-slate-900' : role.color.text}`}>
                            {role.name}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_CATEGORIES.map((category, catIndex) => (
                  <React.Fragment key={category.name}>
                    {/* Category Header */}
                    <tr className="bg-slate-50">
                      <td colSpan={5} className="p-2 px-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          {category.name}
                        </span>
                      </td>
                    </tr>
                    
                    {/* Permission Rows */}
                    {category.permissions.map((permission, permIndex) => (
                      <tr 
                        key={permission} 
                        className={`border-b border-slate-100 ${permIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                      >
                        <td className="p-3 text-slate-700 font-medium">
                          {PERMISSION_LABELS[permission]}
                        </td>
                        {roles.map(roleId => {
                          const { status, label } = getPermissionStatus(roleId, permission);
                          return (
                            <td key={roleId} className="p-3 text-center">
                              {status === 'yes' && (
                                <div className="flex items-center justify-center">
                                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-green-600" />
                                  </div>
                                </div>
                              )}
                              {status === 'assigned' && (
                                <div className="flex flex-col items-center">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="text-[10px] text-blue-600 mt-0.5">Assigned</span>
                                </div>
                              )}
                              {status === 'no' && (
                                <div className="flex items-center justify-center">
                                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                                    <X className="w-4 h-4 text-slate-400" />
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Footer Notes */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-2 text-sm text-blue-800">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p><strong>Project Managers</strong> only see and can manage projects they are specifically assigned to.</p>
                <p><strong>Accountants</strong> and <strong>Investors</strong> have read-only access across all projects.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RoleGuideModal;
